// tslint:disable:max-classes-per-file

import { plainToClass } from 'class-transformer';

import moment from 'moment-timezone';

import {
  EgAction, EgBase, EgTest, EgVirtualMachine, EgApplication, EgDataSource, EgDataSync, EgTestScenario, EgUser, EgApplicationVersion,
  EgApplicationPublisher, EgDevice, EgOperatingSystem, EgOperatingSystemVersion, EgInstallation,
  WsStatisticInterface, EgApplicationProgramInterface, WsReadinessInterface, EgProfileGroupInterface, WsTestInstruction, WsTestOperation, WsTestStatus, WsTestProcess, EgStep
} from '../../core/src/interfaces';

import { loadApi, loadA365, user } from './authConfig';

import { WsReadinessStatus } from './enums';

import * as http from './http';




const delay = process.env.NODE_ENV === 'production' ? 5000 : 8000;




class EgBaseEntity implements EgBase {
  id!: number;
  createdAt!: Date;
  modifiedAt!: Date;
  isActive!: boolean;
}




class EgDataSourceEntity extends EgBaseEntity implements EgDataSource {
  source!: string;
  entity!: string;
  count!: number;
  duration!: string;
  status!: string;
  dataSyncs!: EgDataSyncEntity[];

  static async all(): Promise<EgDataSourceEntity[]> {
    const result: any = await loadApi('/data-sources');

    if (result && result.dataSources) {
      const dataSources: EgDataSourceEntity[] = plainToClass(EgDataSourceEntity, result.dataSources as Object[]);
      return dataSources;
    } else {
      return [];
    }
  }

  static async runSync(dataSource: EgDataSourceEntity) {
    const result: any = await loadApi(`/data-sources/${dataSource.id}/sync`, 'post');

    return result?.dataSource ? true : false;
  }
}




class EgDataSyncEntity extends EgBaseEntity implements EgDataSync {
  dataSource!: EgDataSource;
  status!: number;
  count!: number;

  duration = (): string => {
    // const startedAt = moment(this.createdAt);
    // const endedAt = moment(this.modifiedAt); // moment().add(1000, 'seconds');

    // const duration = moment.duration(endedAt.diff(startedAt));
    // // console.log(duration);

    // const d = moment.duration(endedAt.diff(startedAt)).humanize(false);
    // // console.log(d);

    // return d;
    return '2 seconds';
  };
}




class EgDeviceEntity extends EgBaseEntity implements EgDevice {
  syncId!: number;

  user!: EgUserEntity;
  installations?: EgInstallationEntity[];
  operatingSystem!: EgOperatingSystemEntity;
  operatingSystemVersion!: EgOperatingSystemVersionEntity;
  profileGroups!: EgProfileGroup[];

  name!: string;
  domain!: string;

  static async all(): Promise<EgDeviceEntity[]> {
    const result: any = await loadApi('/devices');

    if (result && result.devices) {
      const devices: EgDeviceEntity[] = plainToClass(EgDeviceEntity, result.devices as Object[]);

      return devices;
    } else {
      return [];
    }
  }

  static async findById(id: number): Promise<any> { // TODO: set type
    const result: any = await loadApi(`/devices/${id}`);

    if (result && result.device) {
      const device: EgDeviceEntity = plainToClass(EgDeviceEntity, result.device as Object);
      return device;
    } else {
      return undefined;
    }
  }

  static filterForServicing(devices: EgDeviceEntity[], servicingVersionNumber: string, servicingType: string = 'windows'): EgDeviceEntity[] {
    if (servicingType === 'proplus') {
      return [];
    } else {
      return devices.filter((device: EgDeviceEntity) => {
        return device.operatingSystemVersion && device.operatingSystemVersion.number < servicingVersionNumber
            && device.operatingSystem && device.operatingSystem.name.toLowerCase().indexOf('microsoft windows 10 pro') > -1;
      });
    }
  }

  static getReleaseGroups = (planningGroups: EgPlanningGroup[], device: EgDeviceEntity) => {
    let groups = '';

    if (planningGroups) {
      planningGroups.forEach((planningGroup: EgPlanningGroup) => {
        planningGroup.releaseGroups.forEach((releaseGroup: EgReleaseGroup) => {
          releaseGroup.devices.forEach((d: any) => {
            if (device.syncId === d.id) {
              groups += `${releaseGroup.name} `;
            }
          });
        });
      });
    }

    return groups;
  }
}




class EgOperatingSystemEntity extends EgBaseEntity implements EgOperatingSystem {
  devices!: [];
  name!: string;
}




class EgOperatingSystemVersionEntity extends EgBaseEntity implements EgOperatingSystemVersion {
  devices!: [];
  number!: string;
  build!: string;
}




class EgActionEntity extends EgBaseEntity implements EgAction {

  // belongs to
  application!: EgApplicationEntity;
  applicationVersion!: EgApplicationVersionEntity;
  operatingSystemVersion!: EgOperatingSystemVersionEntity;
  user!: EgUserEntity;

  // has one
  test?: EgTestEntity;
  newApplicationVersion?: EgApplicationVersion;

  // properties
  operatingSystemVersionNumber!: string;
  appId!: number;
  ownerName!: string;
  type!: number;
  progress!: number;
  details!: string;

  testScenarioId?: number;
  userName?: string;
  userEmail?: string;

  deactivate = async (): Promise<EgActionEntity | undefined> => {
    const result: any = await loadApi(`/actions/${this.id}/deactivate`, 'post', {});
    if (result && result.action) {
      return plainToClass(EgActionEntity, result.action as object);
    } else {
      return undefined;
    }
  }

  duration = (): string => {
    return EgActionEntity.duration(this);
  }

  status = (): string => {
    return EgActionEntity.status(this);
  }

  statusName = (): string => {
    if (this.progress < 0) {
      return 'Failed!';
    } else if (this.progress < 10) {
      return 'Starting...';
    } else if (this.progress < 20) {
      return 'Creating VM...';
    } else if (this.progress < 40) {
      return 'Setting up OS...';
    } else if (this.progress < 60) {
      return 'Installing Application...';
    } else if (this.progress < 100) {
      return 'Running Test Scripts...';
    } else {
      return 'Completed!';
    }
  }

  static duration = (action: EgActionEntity): string => {
    if (action.test) {

      const startedAt = moment(action.test.createdAt);
      const endedAt = moment(action.test.modifiedAt); // moment().add(1000, 'seconds');

      // const duration = moment.duration(endedAt.diff(startedAt));
      // console.log(duration);

      const d = moment.duration(endedAt.diff(startedAt)).humanize(false);
      // console.log(d);

      return d;

    } else {
      return '-';
    }
  }

  static status = (action: EgActionEntity): string => {
    if (action.test && action.test.progress === 100) {
      return 'success';
    } else if (action.test && action.test.progress >= 0 && action.test.progress < 100) {
      return 'active';
    } else {
      return 'error';
    }
  }

  static async all(): Promise<EgActionEntity[] | undefined> {
    const result: any = await loadApi('/actions');
    if (result && result.actions) {
      const actions: EgActionEntity[] = plainToClass(EgActionEntity, result.actions as Object[]);
      return actions;
    } else {
      return undefined;
    }
  }

  static async allPrepare(servicingVersionNumber: string, status: string): Promise<EgActionEntity[] | undefined> {
    const result: any = await loadApi(`/actions/prepare/${servicingVersionNumber}/${status}`);
    if (result && result.actions) {
      const actions: EgActionEntity[] = plainToClass(EgActionEntity, result.actions as Object[]);
      return actions;
    } else {
      return undefined;
    }
  }

  static async allReadiness(servicingVersionNumber?: string): Promise<EgActionEntity[] | undefined> {
    const result: any = await loadApi(servicingVersionNumber ? `/actions/readiness/${servicingVersionNumber}` : '/actions/readiness');
    if (result && result.actions) {
      const actions: EgActionEntity[] = plainToClass(EgActionEntity, result.actions as Object[]);
      return actions;
    } else {
      return undefined;
    }
  }

  static async allActive(servicingVersionNumber?: string) {
    const result: any = await loadApi(servicingVersionNumber ? `/actions/active/${servicingVersionNumber}` : '/actions/active');

    if (result && result.actions) {
      return plainToClass(EgActionEntity, result.actions as Object[]);
    } else {
      return [];
    }
  }

  static async allNotTest() {
    const result: any = await loadApi('/actions/not-test');

    if (result && result.actions) {
      return plainToClass(EgActionEntity, result.actions as Object[]);
    } else {
      return [];
    }
  }

  static async create(selectedAction: any): Promise<EgActionEntity | undefined> {
    const result: any = await loadApi('/actions', 'post', {
      applicationId: selectedAction.appId,
      operatingSystemVersionNumber: selectedAction.operatingSystemVersionNumber,
      type: selectedAction.type,
      testScenarioId: selectedAction.testScenarioId,
      userName: user.name,
      userEmail: user.email
    });

    if (result && result.action) {
      return plainToClass(EgActionEntity, result.action as Object);
    } else {
      return undefined;
    }
  }

  async updateNewApplicationVersion(applicationVersion: EgApplicationVersionEntity): Promise<EgActionEntity> {
    const result: any = await loadApi(`/actions/${this.id}`, 'post', { newApplicationVersionId: applicationVersion.id });
    return result.action;
  }

  static filterForServicing(actions: EgActionEntity[] = [], servicingVersionNumber: string): EgActionEntity[] {
    if (servicingVersionNumber) {
      return actions.filter((action: EgActionEntity) => {
        return servicingVersionNumber === 'all' || action.operatingSystemVersion && action.operatingSystemVersion.number === servicingVersionNumber;
      });
    } else {
      return actions;
    }
  }
}




class EgApplicationEntity extends EgBaseEntity implements EgApplication {

  // belongs to
  publisher!: EgApplicationPublisher;
  owner!: EgUserEntity;
  addInApplication?: EgApplicationEntity;

  // has one
  program!: EgApplicationProgramInterface;
  currentVersion!: EgApplicationVersion;

  // has many
  tests!: EgTestEntity[];
  actions!: EgActionEntity[];
  installations?: EgInstallationEntity[];
  testScenarios!: EgTestScenarioEntity[];
  versions!: EgApplicationVersion[];

  // TODO: readiness
  readiness!: WsReadinessInterface[];

  // properties
  npid!: string;
  externalName!: string;
  name!: string;
  defaultActionId!: number;
  isAddIn!: boolean;
  addInApplicationName!: string;
  actionConfiguration!: {};

  ownerName!: string;
  ownerEmail!: string;
  isAvailable!: boolean;
  available!: string[];
  availability!: string[];

  syncId!: number;
  syncedAt!: Date;

  // instance

  async updateActionConfiguration(value: any): Promise<EgApplicationEntity> {
    const result: any = await loadApi(`/applications/${this.id}`, 'post', { actionConfiguration: value });
    return result.application;
  }

  async updateDefaultAction(defaultActionId: number): Promise<EgApplicationEntity> {
    const result: any = await loadApi(`/applications/${this.id}`, 'post', { defaultActionId });
    return result.application;
  }

  async updateAddInApplication(addInApplicationId: number): Promise<EgApplicationEntity> {
    const result: any = await loadApi(`/applications/${this.id}`, 'post', { addInApplicationId });
    return result.application;
  }

  async updateProgram(program: EgApplicationProgram): Promise<EgApplicationEntity> {
    const result: any = await loadApi(`/applications/${this.id}`, 'post', { programId: program.id });
    return result.application;
  }

  async getInstallations(): Promise<EgInstallationEntity[]> {
    const result: any = await loadApi(`/applications/${this.id}/installations`);
    if (result && result.installations) {
      return plainToClass(EgInstallationEntity, result.installations as Object[]);
    } else {
      return [];
    }
  }

  async getVersions(): Promise<EgApplicationVersionEntity[]> {
    const result: any = await loadApi(`/applications/${this.id}/versions`);
    if (result && result.versions) {
      return plainToClass(EgApplicationVersionEntity, result.versions as Object[]);
    } else {
      return [];
    }
  }

  // static

  static async allPrepare(servicingVersionNumber: string, status: string): Promise<EgApplicationEntity[]> {
    const result: any = await loadApi(`/applications/prepare/${servicingVersionNumber}/${status}`);

    if (result && result.applications) {
      const applications: EgApplicationEntity[] = plainToClass(EgApplicationEntity, result.applications as Object[]);

      const sortedApplications = applications.sort((a: EgApplicationEntity, b: EgApplicationEntity) => {
        if (a.publisher && b.publisher) {
          if (a.publisher.name === b.publisher.name) {
            return a.name > b.name ? 1 : -1;
          } else {
            return a.publisher.name > b.publisher.name ? 1 : -1;
          }
        } else {
          return -1;
        }
      });

      sortedApplications.forEach((application: EgApplicationEntity) => {
        application.actions.sort((a: EgActionEntity, b: EgActionEntity) => a.createdAt < b.createdAt ? 1 : -1);
      });

      return sortedApplications;
    } else {
      return [];
    }
  }

  static async all(filter = 'all'): Promise<EgApplicationEntity[]> {
    console.log("EgApplicationEntity:all");
    const result: any = await loadApi('/applications');

    console.log(result);

    if (result && result.applications) {
      const applications: EgApplicationEntity[] = plainToClass(EgApplicationEntity, result.applications as Object[]);

      const sortedApplications = applications.sort((a: EgApplicationEntity, b: EgApplicationEntity) => {
        if (a.publisher && b.publisher) {
          if (a.publisher.name === b.publisher.name) {
            return a.name > b.name ? 1 : -1;
          } else {
            return a.publisher.name > b.publisher.name ? 1 : -1;
          }
        } else {
          return -1;
        }
      });

      if  (filter === 'all') {
        return sortedApplications;

      } else {
        const filteredApplications = sortedApplications.filter((a: EgApplicationEntity) => {
          if (filter === 'add-ins') {
            return a.isAddIn;
          } else {
            return !a.isAddIn;
          }
        });

        return filteredApplications;
      }

    } else {
      return [];
    }
  }

  static async findById(id: number): Promise<EgApplicationEntity | undefined> {
    const result: any = await loadApi(`/applications/${id}`);

    if (result && result.application) {
      const application: EgApplicationEntity = plainToClass(EgApplicationEntity, result.application as Object);
      return application;
    } else {
      return undefined;
    }
  }

  static filterForServicing(applications: EgApplicationEntity[], servicingVersionNumber: string, servicingType: string = 'windows'): EgApplicationEntity[] {
    return applications.filter((application: EgApplicationEntity) => {
      if (servicingType === 'windows') {
        // windows
        if (application.installations && application.installations.length > 0) {
          return EgInstallationEntity.filterForServicing(application.installations, servicingVersionNumber).length > 0;
        } else {
          return false;
        }

      } else {
        // proplus
        return application.isAddIn;
      }
    });
  }
}




class EgApplicationVersionEntity extends EgBaseEntity implements EgApplicationVersion {

  // belongs to
  application!: EgApplicationEntity;

  // has many
  tests?: EgTestEntity[];
  installations?: EgInstallationEntity[];

  // fields
  architecture!: string;
  number!: string;
  isAvailable!: boolean;
  available!: string[];
  package!: string;
  availability!: string[];
  dependencyAvailability!: string[];
  dependencyIds!: number[];

  description!: string;

  static async all(): Promise<EgApplicationVersionEntity[]> {
    const result: any = await loadApi('/application-versions');

    if (result && result.applicationVersions) {
      const applicationVersions: EgApplicationVersionEntity[] = plainToClass(EgApplicationVersionEntity, result.applicationVersions as Object[]);

      const sortedApplicationVersions = applicationVersions.sort((a: EgApplicationVersionEntity, b: EgApplicationVersionEntity) => {
        if (a.application.publisher.name === b.application.publisher.name) {
          if (a.application.name === b.application.name) {
            return a.number > b.number ? 1 : -1;
          } else {
            return a.application.name > b.application.name ? 1 : -1;
          }
        } else {
          return a.application.publisher.name > b.application.publisher.name ? 1 : -1;
        }
      });

      return sortedApplicationVersions;
    } else {
      return [];
    }
  }
}




class EgInstallationEntity extends EgBaseEntity implements EgInstallation {
  application!: EgApplicationEntity;
  applicationVersion!: EgApplicationVersionEntity;
  device!: EgDeviceEntity;
  operatingSystemVersion!: EgOperatingSystemVersionEntity;

  static filterForServicing(installations: EgInstallationEntity[] | undefined, servicingVersionNumber: string): EgInstallationEntity[] {
    if (installations) {
      return installations.filter((installation: EgInstallationEntity) => installation.operatingSystemVersion && installation.operatingSystemVersion.number < servicingVersionNumber);
    } else {
      return [];
    }
  }
}




class EgApplicationPackage {
  id!: number;
  code!: string;
  manufacturer!: string;
  name!: string;
  version!: string;
  source!: string;
}




class EgApplicationProgram {
  id!: number;
  name!: string;
  source!: string;
  command!: string;
  package!: EgApplicationPackage;

  static async all(): Promise<EgApplicationProgram[]> {
    const result: any = await loadApi('/programs');
    if (result && result.programs) {
      const programs: EgApplicationProgram[] = plainToClass(EgApplicationProgram, result.programs as Object[]);
      const sorted = programs.sort((a: EgApplicationProgram, b: EgApplicationProgram) => {
        if (a.package.manufacturer === b.package.manufacturer) {
          if (a.package.name === b.package.name) {
            return a.name > b.name ? 1 : -1;
          } else {
            return a.package.name > b.package.name ? 1 : -1;
          }
        } else {
          return a.package.manufacturer > b.package.manufacturer ? 1 : -1;
        }
      });
      return sorted;
    } else {
      return [];
    }
  }
}




class EgTestEntity extends EgBaseEntity implements EgTest {

  // belongs to
  application!: EgApplicationEntity;
  applicationVersion!: EgApplicationVersionEntity;
  action!: EgActionEntity;
  testScenario!: EgTestScenarioEntity;

  // has one
  virtualMachine!: EgVirtualMachine;

  steps!: EgStep[];

  currentOperationId!: number;
  currentStep!: number;
  progress!: number;
  details!: string;
  approved!: boolean;
  declined!: boolean;

  screenshotCount!: number;

  assets?: string[];
  operations!: WsTestOperation[];

  process!: WsTestProcess;
  status!: WsTestStatus;

  user?: string;
  admin?: string;

  applicationId?: number;
  applicationVersionId?: number;
  result!: number;
  expireAt?: Date;

  static async fingById(id: number): Promise<EgTestEntity | undefined> {
    const result: any = await loadApi(`/test-processes/${id}`);
    if (result && result.testProcess) {
      const testProcess: EgTestEntity = plainToClass(EgTestEntity, result.testProcess as Object);
      return testProcess;
    } else {
      return undefined;
    }
  }

  static async allPending(): Promise<EgTestEntity[]> {
    const result: any = await loadApi('/tests/pending');

    if (result && result.tests) {
      const tests: EgTestEntity[] = plainToClass(EgTestEntity, result.tests as Object[]);
      return tests;
    } else {
      return [];
    }
  }

  async approve(): Promise<EgTestEntity[]> {
    // console.log('approve');
    const result: any = await loadApi(`/tests/${this.id}/approve`, 'post', {
      // name: fields['name'],
      // automaticallyApproved: fields['automaticallyApproved'],
      // versionId: versionId,
      // ownerId: ownerId
    });

    if (result && result.tests) {
      const tests: EgTestEntity[] = plainToClass(EgTestEntity, result.tests as Object[]);
      return tests;
    } else {
      return [];
    }
  }

  async decline(): Promise<EgTestEntity[]> {
    // console.log('decline');
    const result: any = await loadApi(`/tests/${this.id}/decline`, 'post', {
      // name: fields['name'],
      // automaticallyApproved: fields['automaticallyApproved'],
      // versionId: versionId,
      // ownerId: ownerId
    });

    if (result && result.tests) {
      const tests: EgTestEntity[] = plainToClass(EgTestEntity, result.tests as Object[]);
      return tests;
    } else {
      return [];
    }
  }
}




class EgTestScenarioEntity extends EgBaseEntity implements EgTestScenario {

  // belongs to
  application!: EgApplicationEntity;
  owner!: EgUserEntity;
  version!: EgApplicationVersion;

  // has many
  tests!: EgTest[];

  // properties
  name!: string;
  automaticallyApproved!: boolean;
  domain!: string;
  organizationalUnit!: string;
  account!: string;
  password!: string;
  testScript!: string;
  testCommand!: string;
  startCommand!: string;

  process!: WsTestInstruction[];

  applicationId!: number;
  applicationVersionId!: number;

  static async all(): Promise<EgTestScenarioEntity[]> {
    const result: any = await loadApi('/test-scenarios');

    if (result && result.testScenarios) {
      const testScenarios: EgTestScenarioEntity[] = plainToClass(EgTestScenarioEntity, result.testScenarios as Object[]);
      return testScenarios;
    } else {
      return [];
    }
  }

  static async findById(id: number): Promise<EgTestScenarioEntity | undefined> { // TODO: set type
    const result: any = await loadApi(`/test-scenarios/${id}`);

    if (result && result.testScenario) {
      const testScenario: EgTestScenarioEntity = plainToClass(EgTestScenarioEntity, result.testScenario as Object);
      return testScenario;
    } else {
      return undefined;
    }
  }

  static async createForApplicationVersion(applicationVersionId: number, fields: string[], testScript?: any, versionId?: number, ownerId?: number) {

    const result = await http.post<any>(`/v2/applications/${applicationVersionId}/test-scenarios`, {
      name: fields['name'],
      automaticallyApproved: true,
    });

    if (result && result.testScenario) {

      const testScenario: EgTestScenarioEntity = plainToClass(EgTestScenarioEntity, result.testScenario as Object);

      const formData = new FormData();

      formData.append('testScenarioId', testScenario.id.toString());

      if (testScript) {
        formData.append('testScript', testScript);
      }

      await loadApi(`/test-scripts`, 'post', formData, true);

      return testScenario;
    } else {
      return undefined;
    }
  }

  static async create(applicationId: number, fields: string[], versionId: number, ownerId: number, testScript: any) {
    const result: any = await loadApi(`/applications/${applicationId}/test-scenarios`, 'post', {
      name: fields['name'],
      automaticallyApproved: fields['automaticallyApproved'],
      account: fields['serviceAccount'],
      password: fields['servicePassword'],
      testCommand: fields['testCommand'],
      startCommand: fields['startCommand'],
      versionId: versionId,
      ownerId: ownerId
    });

    if (result && result.testScenario) {
      const testScenario: EgTestScenarioEntity = plainToClass(EgTestScenarioEntity, result.testScenario as Object);

      const formData = new FormData();
      formData.append('testScenarioId', testScenario.id.toString());
      formData.append('testScript', testScript);
      await loadApi(`/test-scripts`, 'post', formData, true);

      return testScenario;
    } else {
      return undefined;
    }
  }

  static async setAutomaticallyApproved(testScenario: EgTestScenarioEntity, b: boolean | undefined) {
    const result: any = await loadApi(`/test-scenarios/${testScenario.id}`, 'post', {
      // name: fields['name'],
      automaticallyApproved: b,
      // versionId: versionId,
      // ownerId: ownerId
    });

    return true;
  }
}




class EgUserEntity extends EgBaseEntity implements EgUser {
  applications!: EgApplicationEntity[];
  devices!: EgDeviceEntity[];

  guid!: string;
  principalName!: string;
  account!: string;
  email!: string;
  name!: string;
  testAccount!: string;
  roleId!: number;
  role!: any;


  static async all(): Promise<EgUserEntity[]> {

    const result: any = await loadApi('/users');

    if (result && result.users) {
      const users: EgUserEntity[] = plainToClass(EgUserEntity, result.users as Object[]);
      return users.sort((a: EgUserEntity, b: EgUserEntity) => {
        if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
        if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
        return 0;
      });
    } else {
      return [];
    }
  }


  static async findById(id: number): Promise<any> { // TODO: set type
    const result: any = await loadApi(`/users/${id}`);

    if (result && result.user) {
      return plainToClass(EgUserEntity, result.user as object);
    } else {
      return undefined;
    }
  }


  static filterForServicing(users: EgUserEntity[], servicingVersionNumber: string, servicingType: string = 'windows'): EgUserEntity[] {
    if (servicingType === 'proplus') {
      // proplus
      return [];

    } else {
      // windows

      return users.filter((user: EgUserEntity) => {
        if (user.devices && user.devices.length > 0) {
          let yes: boolean = false;

          user.devices.forEach((device: EgDeviceEntity) => {
            if (device.installations) {
              const installations: EgInstallationEntity[] = device.installations
                .filter((installation: EgInstallationEntity) => installation.operatingSystemVersion && installation.operatingSystemVersion.number < servicingVersionNumber);

              if (installations.length > 0) {
                yes = true;
              }
            }
          });

          return yes;
        } else {
          return false;
        }
      });
    }
  }
}




class WsNotifications {
  count!: number;
  testApproval!: number;
  applicationReplacement!: number;
  applicationUpgrade!: number;
  applicationRemoval!: number;

  static async load(): Promise<WsNotifications> {
    const result: any = await loadApi('/notifications');

    if (result && result.notifications) {
      const notifications: WsNotifications = plainToClass(WsNotifications, result.notifications as Object);
      return notifications;
    } else {
      return {
        count: 0,
        testApproval: 0,
        applicationReplacement: 0,
        applicationUpgrade: 0,
        applicationRemoval: 0
      };
    }
  }
}




class WsStatistic extends EgBaseEntity implements WsStatisticInterface {
  key!: string;
  value!: string;
  data: { [ key: string ]: any } = {};

  static async all(): Promise<WsStatistic[]> {
    const result: any = await loadApi('/statistics');

    if (result && result.statistics) {
      const statistics: WsStatistic[] = plainToClass(WsStatistic, result.statistics as Object[]);
      return statistics;
    } else {
      return [];
    }
  }
}




class WsReadiness extends EgBaseEntity implements WsReadinessInterface {
  application?: EgApplicationEntity | undefined;
  device?: EgDeviceEntity | undefined;
  operatingSystemVersion!: EgOperatingSystemVersion;
  status!: number;
  number!: string;

  // static

  static async all(servicingVersionNumber: string, type: string): Promise<WsReadiness[]> {
    const result: any = await loadApi(`/readiness/${servicingVersionNumber}/${type}`);
    if (result && result.readiness) {
      return plainToClass(WsReadiness, result.readiness as Object[]);
    } else {
      return [];
    }
  }

  static filterForServicing(items: WsReadiness[], servicingVersionNumber: string): WsReadiness[] {
    return items.filter((item: WsReadiness) => {
      const application: EgApplicationEntity | undefined = item.application;
      if (application && application.installations && application.installations.length > 0) {
        return EgInstallationEntity.filterForServicing(application.installations, servicingVersionNumber).length > 0;
      } else {
        return false;
      }
    });
  }
}




class EgDeployment {

  static async create(deployments: any[]) {

    const result: any = await loadApi(`/deployments`, 'post', { deployments });

    if (result) {
      if (process.env.NODE_ENV === 'development') {
        console.log(result);
      }
      return result;
    } else {
      return undefined;
    }
  }
}




class EgProfileGroup extends EgBaseEntity implements EgProfileGroupInterface {
  // belongs to
  owner!: EgUserEntity;
  parent!: EgProfileGroup;

  // has many
  groups!: EgProfileGroup[];
  devices!: EgDeviceEntity[];

  // properties
  parentId!: number;
  name!: string;
  isExpanded!: boolean;

  // static
  static async all(): Promise<EgProfileGroup[]> {
    const result: any = await loadApi('/profile-groups');
    if (result && result.profileGroups) {
      return plainToClass(EgProfileGroup, result.profileGroups as Object[]);
    } else {
      return [];
    }
  }

  static global(profileGroups: EgProfileGroup[]): EgProfileGroup[] {
    profileGroups.forEach((group: EgProfileGroup) => {
      group.groups = profileGroups.filter((g: EgProfileGroup) => g.parentId === group.id);
    });

    profileGroups.forEach((group: EgProfileGroup) => {
      group.devices = getDevices(group);
    });

    const global = profileGroups.find((g: EgProfileGroup) => !g.parentId);

    if (global) {
      global.isExpanded = true;
      return [ global ];
    } else {
      return [];
    }
  }

  static async create(state: any) {
    const result: any = await loadApi(`/profile-groups`, 'post', {
      profileGroup: {
        name: state.fields['groupName'],
        parentId: state.parentId,
        ownerId: state.ownerId,
        criteriaDeviceName: state.fields['deviceName']
      }
    });

    if (result && result.profileGroup) {
      return plainToClass(EgProfileGroup, result.profileGroup as Object);
    } else {
      return undefined;
    }
  }
}




const getDevices = (profileGroup: EgProfileGroup): EgDeviceEntity[] => {
  const devices = profileGroup.devices;

  profileGroup.groups.forEach((g: EgProfileGroup) => {
    mergeByProperty(devices, getDevices(g), 'id');
  });

  return devices;
};




const mergeByProperty = (target, source, prop) => {
  source.forEach(sourceElement => {
    let targetElement = target.find(targetElement => {
      return sourceElement[prop] === targetElement[prop];
    })
    targetElement ? Object.assign(targetElement, sourceElement) : target.push(sourceElement);
  });
};




class EgPlanningGroup {
  id!: number;
  name!: string;
  wave!: string;
  priority!: string;
  releaseGroups!: EgReleaseGroup[];
  isExpanded!: boolean;

  static async all(): Promise<EgPlanningGroup[]> {
    return [];
    // const result: any = await loadApi('/planning-groups');
    // if (result && result.planningGroups) {
    //   const planningGroups: EgPlanningGroup[] = plainToClass(EgPlanningGroup, result.planningGroups as Object[]);
    //   // const sorted = programs.sort((a: EgApplicationProgram, b: EgApplicationProgram) => {
    //   //   if (a.package.manufacturer === b.package.manufacturer) {
    //   //     if (a.package.name === b.package.name) {
    //   //       return a.name > b.name ? 1 : -1;
    //   //     } else {
    //   //       return a.package.name > b.package.name ? 1 : -1;
    //   //     }
    //   //   } else {
    //   //     return a.package.manufacturer > b.package.manufacturer ? 1 : -1;
    //   //   }
    //   // });
    //   return planningGroups;
    // } else {
    //   return [];
    // }
  }
}




class EgReleaseGroup {
  id!: number;
  name!: string;
  devices!: any[];

  static async all(): Promise<EgReleaseGroup[]> {
    return [];
    // const result: any = await loadApi('/release-groups');
    // if (result && result.releaseGroups) {
    //   const releaseGroups: EgReleaseGroup[] = plainToClass(EgReleaseGroup, result.releaseGroups as Object[]);
    //   // const sorted = programs.sort((a: EgApplicationProgram, b: EgApplicationProgram) => {
    //   //   if (a.package.manufacturer === b.package.manufacturer) {
    //   //     if (a.package.name === b.package.name) {
    //   //       return a.name > b.name ? 1 : -1;
    //   //     } else {
    //   //       return a.package.name > b.package.name ? 1 : -1;
    //   //     }
    //   //   } else {
    //   //     return a.package.manufacturer > b.package.manufacturer ? 1 : -1;
    //   //   }
    //   // });
    //   return releaseGroups;
    // } else {
    //   return [];
    // }
  }
}




let notifications: WsNotifications | undefined;

async function loadNotifications() {
  notifications = await WsNotifications.load();
  setTimeout(loadNotifications, process.env.NODE_ENV === 'production' ? 5000 : 30000);
}




class Application {

  static async all(filter?: any) {
    const payload = {"take":500,"skip":0,"page":1,"pageSize":500,"sort":null,"group":[],"UserNames":[],"ApplicationNames":[],"DeviceCodes":[],"OSVersionID":null,"ApplicationStatus":null,"RiskID":null,"IsActive":true,"TagIds":[],"ImportanceIds":[],"sorts":[]};
    let results =  await loadA365('/Windows10Applications/SearchProfilesByWindows10Application', payload);

    let applications = [];

    if (results) {
      applications = results.map((application: any) => {
        if (!application['UserCount']) {
          const random = Math.floor(Math.random() * 10) + 1;
          application['UserCount'] =  random;
        }
        return application;
      });
    }

    if (filter) {
      return applications.filter((application: any) => filter(application));
    } else {
      return applications;
    }
  }

  static async findOne(id: number) {
    const payload = {"take":500,"skip":0,"page":1,"pageSize":500,"sort":null,"group":[],"UserNames":[],"ApplicationNames":[],"DeviceCodes":[],"OSVersionID":null,"ApplicationStatus":null,"RiskID":null,"IsActive":true,"TagIds":[],"ImportanceIds":[],"sorts":[]};
    const applications = await loadA365('/Windows10Applications/SearchProfilesByWindows10Application', payload);

    const application = applications.find((application) => {
      return application.ID === id;
    });

    return application;
  }

  static async normalizationVersionsSoftwareNames() {
    const payload = {"take":500,"skip":0,"page":1,"pageSize":500,"sort":null,"group":[],"ExternalSoftware":null,"NormalizedSoftware":null,"sorts":[]};
    const results =  await loadA365('/ApplicationNormalization/SearchSoftwareNames', payload);

    return results;
  };

  static async normalizationVersionsApplications() {
    const payload = {"take":500,"skip":0,"page":1,"pageSize":500,"sort":null,"group":[],"ExternalApplications":[],"NormalizedApplications":[],"NormalizationStatusIDs":[],"DataSources":[],"sorts":[]};
    const results =  await loadA365('/ApplicationNormalization/SearchApplications', payload);

    return results;
  };
}

class Device {
  static async all() {
    const payload = {"take":500,"skip":0,"page":1,"pageSize":500,"sort":null,"group":[],"UserNames":[],"DeviceNames":[],"OSVersionID":null,"InventoryStatusId":1,"ManufacturerID":null,"ModelID":null,"ChannelID":null,"DeploymentRingID":null,"DeploymentRingCollectionID":null,"TargetOSVersionID":null,"TagIds":[],"sorts":[]};
    return loadA365('/Windows10Devices/SearchProfilesByWindows10Device', payload);
  }
}

class ManagedProduct {
  static all() {
    return [
      { id: 1, name: 'Windows 10', type: 'Operating System',    owner: 'Douglas Wood',   manager: 'Maris Balodis', approach: 'Operating System' },
      { id: 2, name: 'Office 365', type: 'Desktop Application', owner: 'David A. Lambe', manager: 'Maris Balodis', approach: 'Desktop Application' },
      { id: 3, name: 'SharePoint', type: 'Web Application',     owner: 'David A. Lambe', manager: 'Maris Balodis', approach: 'Web Application' },
      { id: 4, name: 'Outlook',    type: 'Mobile Application',  owner: 'David A. Lambe', manager: 'Maris Balodis', approach: 'Mobile Application' }
    ];
  }
}

class Replacement {
  static all() {
    return [
      { id: 1, name: 'Flash Player',   replacement: 'Google Chrome', reason: 'Not available' },
      { id: 2, name: 'Critical App',   replacement: 'Other App',     reason: 'Incompatible'  }
    ];
  }
}

class User {
  static async all() {
    const payload = {"take":500,"skip":0,"page":1,"pageSize":500,"sort":null,"group":[],"UserNames":[],"CountryId":null,"SiteId":null,"DepartmentId":null,"VipStatusId":null,"IsActive":true,"TagIds":[],"sorts":[]};
    const users = await loadA365('/Windows10Users/SearchProfilesByWindows10User', payload);
    return users.filter((user: any) => user.UserName.toLowerCase().indexOf('evg') < 0);
  }
}

enum WsActionType {
  None    = 0,
  Test    = 1,
  Defer   = 2,
  Upgrade = 3,
  Replace = 4,
  Remove  = 5
}




const getReadinessLink = (url: string, s: string): string => {

  if (s === 'Awaiting Action') {
    return `${url.replace('/prepare', '/evaluate/applications')}`;
  } else if (s === 'Awaiting Approval') {
    return '/workflows/test-pending';
  } else if (s === 'In Progress') {
    return `${url}/test-running`;
  } else if (s === 'Testing in Progress') {
    return `${url}/apps/test-running`;
  } else if (s === 'Testing Passed') {
    return `${url}/apps/test-passed`;
  } else if (s === 'Passed') {
    return `${url}/test-passed`;
  } else if (s === 'Failed') {
    return `${url}/test-failed`;
  } else if (s === 'Testing Failed') {
    return `${url}/test-failed`;


  } else if (s === 'Deferred') {
    return `${url}/apps/defer`;
  } else if (s === 'Remediated') {
    return `${url}/apps/remediate`;


  } else if (s === 'Requires Upgrade') {
    // return `${url}/upgrade-pending`;
    return `${url}/workflows/application-upgrade`;
  } else if (s === 'Requires Replacement') {
    // return `${url}/replace-pending`;
    return `${url}/workflows/application-replace`;    
  } else if (s === 'Awaiting Removal') {
    // return `${url}/remove-pending`;
    return `${url}/workflows/application-remove`;


  } else if (s === 'Replaced') {
    return `${url}/replace-completed`
  } else if (s === 'Upgraded') {
    return `${url}/upgrade-completed`;
  } else if (s === 'Removed') {
    return `${url}/remove-completed`;

  } else if (s === 'Ready by Default') {
    return `${url}`;

  } else {
    return '';
  }
};

const filterReadiness = (status: WsReadinessStatus, items: WsReadiness[]) => {
  return items.filter((item: WsReadiness) => item.status === status);
};




export {
  getReadinessLink,

  Application,
  Device,
  ManagedProduct,
  Replacement,
  User,

  delay,
  EgApplicationEntity,
  EgApplicationPackage,
  EgApplicationProgram,
  EgApplicationVersionEntity,
  EgActionEntity,
  EgBaseEntity,
  EgDataSourceEntity,
  EgDataSyncEntity,
  EgDeviceEntity,
  EgInstallationEntity,
  EgTestEntity,
  EgTestScenarioEntity,
  EgUserEntity,

  EgPlanningGroup,
  EgReleaseGroup,

  WsNotifications,
  WsReadiness,
  WsStatistic,

  notifications,
  loadNotifications,
  // socket,

  WsActionType,
  EgProfileGroup,
  EgDeployment
};
